// Wires the ported weapon system into longfight's combat model:
//  • EnemyField — the player's shots ray-test the RED army + tanks and damage them (points on hit/kill).
//  • PlayerCombatSide — a one-unit CombatSide that makes the player a TARGET the red army + tanks shoot
//    (registered with the projectile system + both enemy acquire loops). Only ranged soldiers + tanks
//    target it (Army.acquire gates melee out), so melee brawlers/giants ignore you.
//  • PlayerCombat — the per-frame orchestrator: fire → viewmodel/recoil/points, shop buys, death/respawn.

import * as THREE from "three";
import { PLAYER } from "../config";
import { POINTS, STARTING_WEAPON } from "../data/arsenal";
import type { Combatant, CombatSide, DamageType, Projectiles } from "./projectiles";
import type { Army } from "../entities/army";
import type { Tanks } from "../entities/tanks";
import type { Player } from "../entities/player";
import type { Input } from "../engine/input";
import type { NavGraph } from "./pathfind";
import type { PlayerHud } from "../ui/playerhud";
import { WeaponSystem, type EnemyField } from "./weapons";
import { ViewModel } from "../entities/viewmodel";
import { Shop } from "./shop";

const _rel = new THREE.Vector3();

/** Ray-tests the player's shots against the red army + tanks, damaging the nearest hit. */
class RedField implements EnemyField {
  constructor(private army: Army, private tanks: Tanks) {}

  hitscan(origin: THREE.Vector3, dir: THREE.Vector3, damage: number, range: number) {
    let bestT = range;
    let bestSide: CombatSide | null = null;
    let bestUnit: Combatant | null = null;
    let killPts = 0;
    const test = (u: Combatant, mid: number, radius: number, side: CombatSide, kp: number): void => {
      _rel.set(u.pos.x - origin.x, u.pos.y + mid - origin.y, u.pos.z - origin.z);
      const tca = _rel.dot(dir);
      if (tca < 0 || tca > bestT) return;
      const d2 = _rel.lengthSq() - tca * tca;
      const r = radius + 8; // small aim assist
      if (d2 > r * r) return;
      bestT = tca;
      bestUnit = u;
      bestSide = side;
      killPts = kp;
    };
    for (const u of this.army.units()) {
      if (u.team !== 1 || u.dead) continue;
      const f = u as Combatant & { kind?: string; h?: number };
      const h = f.h ?? 24;
      test(u, h * 0.5, Math.max(u.hitR, h * 0.55), this.army, f.kind === "giant" ? POINTS.killGiant : POINTS.killLittle);
    }
    for (const u of this.tanks.units()) {
      if (u.team !== 1 || u.dead || u.active === false) continue;
      test(u, 24, Math.max(u.hitR, 30), this.tanks, POINTS.killTank);
    }
    if (!bestUnit) return { hit: false, killed: false, points: 0, wallDist: null };
    const wasDead = (bestUnit as Combatant).dead;
    bestSide!.damage(bestUnit, damage, "bullet");
    const killed = !wasDead && ((bestUnit as Combatant).dead || (bestUnit as Combatant).hp <= 0);
    return { hit: true, killed, points: POINTS.hit + (killed ? killPts : 0), wallDist: null };
  }

  meleeHit(origin: THREE.Vector3, dir: THREE.Vector3, range: number, arc: number, damage: number) {
    const cos = Math.cos(arc);
    let points = 0;
    let kills = 0;
    let hits = 0;
    const swing = (u: Combatant, side: CombatSide, kp: number): void => {
      if (u.team !== 1 || u.dead) return;
      const dx = u.pos.x - origin.x;
      const dz = u.pos.z - origin.z;
      const dist = Math.hypot(dx, dz);
      if (dist > range + u.hitR) return;
      if ((dx / (dist || 1)) * dir.x + (dz / (dist || 1)) * dir.z < cos) return;
      hits++;
      const wasDead = u.dead;
      side.damage(u, damage, "melee");
      const killed = !wasDead && (u.dead || u.hp <= 0);
      points += POINTS.hit + (killed ? kp : 0);
      if (killed) kills++;
    };
    for (const u of this.army.units()) {
      const f = u as Combatant & { kind?: string };
      swing(u, this.army, f.kind === "giant" ? POINTS.killGiant : POINTS.killLittle);
    }
    for (const u of this.tanks.units()) {
      if (u.active === false) continue;
      swing(u, this.tanks, POINTS.killTank);
    }
    return { points, kills, hits };
  }
}

/** The player as a single enemy Combatant the RED side targets + damages. */
class PlayerCombatSide implements CombatSide {
  active = false;
  readonly unit: Combatant;
  constructor(private player: Player) {
    // starts inactive/dead so nobody targets a stale (0,0,0) ghost before play begins
    this.unit = { team: 0, pos: new THREE.Vector3(), vel: new THREE.Vector3(), hp: player.maxHealth, dead: true, hitR: 16, active: false };
  }
  /** Enter play: place the unit at the player's CURRENT spot before it becomes targetable. */
  enter(): void {
    this.sync();
    this.active = true;
    this.unit.active = true;
    this.unit.dead = false;
  }
  /** Leave play: mark the unit dead+inactive so any enemy holding it as a target drops it
   *  (army fighters read target.active/dead directly, not units(), so this must be on the unit). */
  leave(): void {
    this.active = false;
    this.unit.active = false;
    this.unit.dead = true;
  }
  sync(): void {
    const p = this.player;
    this.unit.pos.set(p.position.x, p.position.y - PLAYER.eyeHeight, p.position.z);
    this.unit.vel.set(p.velocityX, 0, p.velocityZ);
    this.unit.hp = p.health;
    this.unit.dead = !p.alive;
  }
  units(): readonly Combatant[] {
    return this.active && this.player.alive ? [this.unit] : [];
  }
  damage(_u: Combatant, dmg: number, _type: DamageType): void {
    this.player.damage(dmg);
  }
}

export class PlayerCombat {
  readonly weapons = new WeaponSystem(STARTING_WEAPON);
  private field: RedField;
  private side: PlayerCombatSide;
  private view: ViewModel;
  readonly shop: Shop;
  points: number = POINTS.start;
  private message = "";
  private messageT = 0;

  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    private player: Player,
    army: Army,
    tanks: Tanks,
    private projectiles: Projectiles,
    private input: Input,
    private hud: PlayerHud,
  ) {
    this.field = new RedField(army, tanks);
    this.side = new PlayerCombatSide(player);
    this.view = new ViewModel(camera);
    this.shop = new Shop(scene, player, this.weapons);
    // register the player as a projectile target + an acquirable enemy for the red army & tanks
    projectiles.addSide(this.side);
    army.addTargetSide(this.side);
    tanks.addTargetSide(this.side);
  }

  /** (Re)build the shop for a level; returns the collider data (wall AABBs + off-nav floor rect) + spawn. */
  onLevel(nav: NavGraph): { walls: Shop["walls"]; spawn: THREE.Vector3; footprint: Shop["footprint"]; floorY: number } {
    this.shop.build(nav);
    return { walls: this.shop.walls, spawn: this.shop.spawn, footprint: this.shop.footprint, floorY: this.shop.floorY };
  }

  enter(): void {
    this.view.setFov(PLAYER.fov); // gun perspective matches the wider play-mode FOV
    this.view.setWeapon(this.weapons.currentDef, this.weapons.papLevel);
    this.weapons.changed = false;
    this.side.enter(); // syncs the unit to the (already-respawned) player, then activates it
  }
  exit(): void {
    this.side.leave(); // deactivate + mark dead so red fighters drop the target
  }

  /** Per-frame combat tick (called from updatePlayer while playing). Returns true if the player died. */
  update(dt: number): boolean {
    this.side.sync();

    const shot = this.weapons.update(this.player, this.input, this.field);
    if (shot.pointsEarned) this.points += shot.pointsEarned;
    if (shot.fired) {
      if (shot.melee) {
        this.view.meleeSmash();
      } else if (shot.deploy && shot.deployOrigin && shot.deployDir) {
        this.lobDeploy(shot.deployOrigin, shot.deployDir, shot.deployDamage ?? 100, shot.deployCount ?? 1);
        this.view.throwAnim();
      } else {
        this.view.fire();
        this.player.addRecoil(0.014);
      }
    }
    if (this.weapons.changed) {
      this.view.setWeapon(this.weapons.currentDef, this.weapons.papLevel);
      this.weapons.changed = false;
    }
    this.view.setReloading(this.weapons.hud.reloading);
    this.view.setHeldMotion(this.player.velocityX, this.player.velocityZ, this.player.velocityY);
    this.view.update(dt);

    // ---- shop ----
    this.shop.update();
    if (this.input.consumeKeyPress("KeyE")) {
      const res = this.shop.tryBuy(this.points);
      if (res.ok) this.points -= res.spent;
      if (this.weapons.changed) {
        this.view.setWeapon(this.weapons.currentDef, this.weapons.papLevel);
        this.weapons.changed = false;
      }
      if (res.message) this.setMessage(res.message);
    }

    // ---- HUD ----
    if (this.messageT > 0) this.messageT -= dt;
    this.hud.setHealth(this.player.health / Math.max(1, this.player.maxHealth));
    this.hud.setPoints(this.points);
    this.hud.setWeapon(this.weapons.hud.name, this.weapons.hud.mag, this.weapons.hud.reserve, this.weapons.hud.reloading);
    this.hud.setPrompt(this.shop.prompt);
    this.hud.setMessage(this.messageT > 0 ? this.message : "");

    // ---- death ----
    if (!this.player.alive) {
      this.points = Math.floor(this.points / 2); // soft penalty; keep weapons + perks
      this.setMessage("You went down — respawned (points halved)");
      return true;
    }
    return false;
  }

  render(renderer: THREE.WebGLRenderer): void {
    this.view.render(renderer);
  }

  private setMessage(m: string): void {
    this.message = m;
    this.messageT = 2.5;
  }

  private lobDeploy(origin: THREE.Vector3, dir: THREE.Vector3, dmg: number, count: number): void {
    const feetY = this.player.position.y - PLAYER.eyeHeight;
    for (let i = 0; i < count; i++) {
      const spread = (i - (count - 1) / 2) * 0.18;
      const dx = dir.x * Math.cos(spread) - dir.z * Math.sin(spread);
      const dz = dir.x * Math.sin(spread) + dir.z * Math.cos(spread);
      const tx = this.player.position.x + dx * 900;
      const tz = this.player.position.z + dz * 900;
      this.projectiles.fireArc(origin.x, origin.z, feetY, tx, tz, 0, dmg, 130, 180, null);
    }
  }
}
